import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams, useParams } from 'react-router-dom';
import PageLayout from '@/components/PageLayout';
import { useCar } from '@/contexts/CarContext';
import { useCarSelectionContext } from '@/contexts/CarSelectionContext';
import { useExpenseCategories } from '@/contexts/ExpenseCategoriesContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CalendarIcon, CalendarSync, Car, Euro, FileText, MapPin, Clock, ChevronDown, X, Search, Check, Info, Droplets, ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useI18n } from '@/i18n/LanguageContext';
import { useSettings } from '@/contexts/SettingsContext';
import { FALLBACK_CATEGORIES } from '@/constants/expenseCategories';
import { toast } from '@/components/ui/use-toast';
import { uploadCarImage } from '@/lib/uploadCarImage';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import { format } from 'date-fns';

// Categories will be loaded from database via context

interface CategoryMultiSelectProps {
  value: string[];
  onChange: (value: string[]) => void;
  categories: Array<{ value: string; label: string; icon_path: string | null }>;
}

const CategoryMultiSelect = ({ value = [], onChange, categories }: CategoryMultiSelectProps) => {
  const { t: tCategory } = useI18n();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [local, setLocal] = useState(value);

  const sorted = useMemo(() =>
    [...(categories || [])].sort((a, b) => {
      // Put "Other" at the end
      if (a.value === 'Other') return 1;
      if (b.value === 'Other') return -1;
      // Sort by translated label, respecting active language
      const labelA = tCategory(`category.${a.value}`) || a.label;
      const labelB = tCategory(`category.${b.value}`) || b.label;
      return labelA.localeCompare(labelB);
    })
  , [categories, tCategory]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return sorted;
    return sorted.filter((c) => {
      const translatedLabel = tCategory(`category.${c.value}`) || c.label;
      return translatedLabel.toLowerCase().includes(q);
    });
  }, [query, sorted, tCategory]);

  const toggle = (val: string) => {
    setLocal((prev) => prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]);
  };

  const apply = () => { onChange(local); setOpen(false); };
  const clear = () => { setLocal([]); };
  const cancel = () => { setLocal(value); setOpen(false); };

  return (
    <div>
      <button
        type="button"
        onClick={() => { setLocal(value); setOpen(true); }}
        className="w-full h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm flex items-center justify-between hover:bg-gray-50"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className="truncate text-gray-700 flex items-center gap-2">
          {value.length === 1 ? (
            <>
              {(() => {
                const cat = categories.find(c => c.value === value[0]);
                return cat?.value !== 'Other' && cat?.icon_path ? (
                  <img src={cat.icon_path} alt={tCategory(`category.${cat.value}`)} className="w-4 h-4" />
                ) : null;
              })()}
              {(() => {
                const cat = categories.find(c => c.value === value[0]);
                return cat ? (tCategory(`category.${cat.value}`) || cat.label) : value[0];
              })()}
            </>
          ) : value.length > 1 ? (
            tCategory('addCost.selected', { count: value.length })
          ) : (
            tCategory('addCost.chooseCategories')
          )}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-500" />
      </button>

      {open && (
        <div className="fixed inset-0 z-[60]">
          <div className="absolute inset-0 bg-black/20" onClick={cancel} />
          <div className="absolute inset-x-3 bottom-3 top-16 max-w-2xl mx-auto bg-white rounded-2xl shadow-xl flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-900">{tCategory('common.selectCategories')}</h3>
              <button type="button" onClick={cancel} className="p-1.5 rounded-lg hover:bg-gray-100" aria-label={tCategory('actions.close')}>
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-4 pt-3 pb-2 sticky top-0 bg-white">
              <div className="relative">
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={tCategory('common.searchCategories')}
                  className="w-full h-10 rounded-xl border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-[#7c9ffa]/40"
                />
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            <div className="px-2 pb-2 overflow-auto">
              <ul className="divide-y divide-gray-100 rounded-xl border border-gray-200">
                {filtered.map((cat) => {
                  const selected = local.includes(cat.value);
                  return (
                    <li key={cat.value}>
                      <button
                        type="button"
                        onClick={() => toggle(cat.value)}
                        className={cn(
                          "w-full flex items-center justify-between px-3 py-2.5 text-left",
                          selected ? "bg-[#eff2fc]" : "bg-white hover:bg-gray-50"
                        )}
                      >
                        <div className="flex items-center gap-3">
                          {cat.value !== 'Other' && cat.icon_path && <img src={cat.icon_path} alt={tCategory(`category.${cat.value}`)} className="w-5 h-5" />}
                          <span className="text-sm text-gray-800">{tCategory(`category.${cat.value}`) || cat.label}</span>
                        </div>
                        <span className={cn("inline-flex items-center justify-center w-5 h-5 rounded", selected ? "bg-[#7c9ffa]" : "border border-gray-300")}
                          aria-hidden>
                          {selected && <Check className="w-3.5 h-3.5 text-white" />}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>

            <div className="mt-auto px-4 py-3 border-t border-gray-200 flex gap-2 justify-between">
              <button type="button" onClick={clear} className="text-sm text-gray-600 hover:text-gray-800 flex-1 text-center">{tCategory('actions.clear')}</button>
              <Button type="button" variant="secondary" onClick={cancel} className="px-3 py-2 text-xs h-7 flex-1">{tCategory('actions.cancel')}</Button>
              <Button type="button" onClick={apply} className="px-3 py-2 text-xs h-7 flex-1">{tCategory('actions.apply')}</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const AddCostPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { expenseId } = useParams<{ expenseId?: string }>();
  const { cars } = useCar();
  const { currentCar } = useCarSelectionContext();
  const expenseCategoriesResult = useExpenseCategories?.();
  const { categories: dbCategories = [], loading: categoriesLoading = false, error: categoriesError = null } = expenseCategoriesResult || {};
  const { t, formatDate: formatDateI18n, formatCurrency, currency } = useI18n();
  const { settings } = useSettings();
  
  // Prefer DB categories when available, otherwise use fallback
  const categoriesToUse = (dbCategories && dbCategories.length > 0) ? dbCategories : FALLBACK_CATEGORIES;
  
  // Show non-blocking hint if table is missing, but don't block the page
  useEffect(() => {
    if ((categoriesError as any)?.code === '42P01') {
      console.info('[AddCostPage] Expense categories table not found, using fallback categories');
    }
  }, [categoriesError]);
  
  // Debug logging
  useEffect(() => {
    console.log('AddCostPage - Categories:', dbCategories.length, 'Loading:', categoriesLoading, 'Error:', categoriesError);
  }, [dbCategories, categoriesLoading, categoriesError]);
  
  // Form state
  const [selectedCarId, setSelectedCarId] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [amount, setAmount] = useState<string>('');
  const [categoryAmounts, setCategoryAmounts] = useState<Record<string, string>>({});
  const [description, setDescription] = useState<string>('');
  const [expenseDate, setExpenseDate] = useState<Date>(new Date());
  const [odometerReading, setOdometerReading] = useState<string>('');
  const [lastOdometerReading, setLastOdometerReading] = useState<number | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [currentImageIndex, setCurrentImageIndex] = useState<number>(-1);
  const [imageZoom, setImageZoom] = useState(1);
  const [imageOffset, setImageOffset] = useState({ x: 0, y: 0 });
  const panStartRef = useRef<{ x: number; y: number } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>('');
  const [showOdometerInfo, setShowOdometerInfo] = useState(false);
  const [fuelVolume, setFuelVolume] = useState<string>('');
  const [tankFullyFilled, setTankFullyFilled] = useState<boolean>(false);
  const [showTankFilledInfo, setShowTankFilledInfo] = useState(false);
  const [recurringDrawerOpen, setRecurringDrawerOpen] = useState(false);
  /** Which line is being edited in the recurring drawer: '_single' for 0/1 category, or category value for multi-line */
  const [recurringDrawerFor, setRecurringDrawerFor] = useState<string | null>(null);
  /** Per-line recurrence: key = '_single' or category; value has repeat + start/end dates */
  const [recurringByLine, setRecurringByLine] = useState<Record<string, { repeat: string; startDate: string; endDate: string }>>({});
  /** Draft values in the recurring drawer; Apply commits to recurringByLine */
  const [recurringDrawerDraft, setRecurringDrawerDraft] = useState<{ repeat: string; startDate: string; endDate: string }>({ repeat: '', startDate: '', endDate: '' });
  /** One document per recurring line (e.g. contract); stored once on rule, linked to each materialized expense */
  const [recurringReceiptByLine, setRecurringReceiptByLine] = useState<Record<string, File | null>>({});
  const [isFromBottomNav, setIsFromBottomNav] = useState<boolean>(false);
  const [existingReceiptUrl, setExistingReceiptUrl] = useState<string | null>(null);
  const [existingReceiptPath, setExistingReceiptPath] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [showInsuranceDetailsPrompt, setShowInsuranceDetailsPrompt] = useState(false);

  // Image handling functions
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const recurringFileInputRef = React.useRef<HTMLInputElement>(null);
  const recurringUploadForLineRef = React.useRef<string | null>(null);

  const handleAddImageClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle ESC key, arrow keys, and zoom for full screen image
  useEffect(() => {
    if (!fullScreenImage) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setFullScreenImage(null);
        setCurrentImageIndex(-1);
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowLeft' && currentImageIndex > 0) {
        const newIndex = currentImageIndex - 1;
        setCurrentImageIndex(newIndex);
        setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      } else if (e.key === 'ArrowRight' && currentImageIndex < imageFiles.length - 1) {
        const newIndex = currentImageIndex + 1;
        setCurrentImageIndex(newIndex);
        setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
        setImageZoom(1);
        setImageOffset({ x: 0, y: 0 });
      }
    };

    const handleWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('wheel', handleWheel, { passive: false });
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('wheel', handleWheel);
      document.body.style.overflow = 'unset';
    };
  }, [fullScreenImage, currentImageIndex, imageFiles]);

  const handleImageInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    const files = Array.from(e.target.files);
    const availableSlots = 3 - imageFiles.length;
    const filesToAdd = files.slice(0, availableSlots);
    setImageFiles([...imageFiles, ...filesToAdd]);
    e.target.value = '';
  };

  const handleRemoveImage = (index: number) => {
    const newFiles = [...imageFiles];
    newFiles.splice(index, 1);
    setImageFiles(newFiles);
  };

  const openRecurringFilePicker = (lineKey: string) => {
    recurringUploadForLineRef.current = lineKey;
    recurringFileInputRef.current?.click();
  };

  // Sync recurring drawer draft when opening for a line
  useEffect(() => {
    if (recurringDrawerOpen && recurringDrawerFor !== null) {
      const current = recurringByLine[recurringDrawerFor];
      setRecurringDrawerDraft(current ?? { repeat: '', startDate: '', endDate: '' });
    }
  }, [recurringDrawerOpen, recurringDrawerFor]);

  const handleRecurringDrawerApply = () => {
    if (recurringDrawerFor !== null) {
      setRecurringByLine(prev => ({ ...prev, [recurringDrawerFor]: recurringDrawerDraft }));
    }
    setRecurringDrawerOpen(false);
    setRecurringDrawerFor(null);
  };

  const handleRecurringDrawerClose = () => {
    setRecurringDrawerOpen(false);
    setRecurringDrawerFor(null);
  };

  const handleRecurringFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const lineKey = recurringUploadForLineRef.current;
    recurringUploadForLineRef.current = null;
    if (!lineKey || !e.target.files?.length) return;
    setRecurringReceiptByLine(prev => ({ ...prev, [lineKey]: e.target.files![0] }));
    e.target.value = '';
  };

  const removeRecurringReceipt = (lineKey: string) => {
    setRecurringReceiptByLine(prev => ({ ...prev, [lineKey]: null }));
  };

  // Normalize decimal input (comma to period for LV keyboards)
  const normalizeDecimalInput = (value: string): string => {
    return value.replace(',', '.');
  };

  // Normalize fuel volume input with up to 3 decimal places
  const normalizeFuelVolumeInput = (value: string): string => {
    // Replace comma with period for LV keyboards
    let normalized = value.replace(',', '.');
    
    // Remove any non-numeric characters except decimal point
    normalized = normalized.replace(/[^\d.]/g, '');
    
    // Ensure only one decimal point
    const parts = normalized.split('.');
    if (parts.length > 2) {
      normalized = parts[0] + '.' + parts.slice(1).join('');
    }
    
    // Limit to 3 decimal places
    if (parts.length === 2 && parts[1].length > 3) {
      normalized = parts[0] + '.' + parts[1].substring(0, 3);
    }
    
    return normalized;
  };

  // Handle category amount changes
  const handleCategoryAmountChange = (category: string, value: string) => {
    const normalized = normalizeDecimalInput(value);
    setCategoryAmounts(prev => ({
      ...prev,
      [category]: normalized
    }));
  };

  // When adding a second (or more) category, preserve the existing single-category amount, recurrence, and recurring document
  const handleCategoriesChange = (newCategories: string[]) => {
    const wasSingle = categories.length === 1;
    const nowMultiple = newCategories.length > 1;
    if (wasSingle && nowMultiple && newCategories.includes(categories[0])) {
      const prevCat = categories[0];
      setCategoryAmounts(prev => ({ ...prev, [prevCat]: amount }));
      if (recurringByLine['_single']) {
        setRecurringByLine(prev => ({ ...prev, [prevCat]: prev['_single'] }));
      }
      if (recurringReceiptByLine['_single']) {
        setRecurringReceiptByLine(prev => ({ ...prev, [prevCat]: prev['_single'] }));
      }
    }
    setCategories(newCategories);
  };

  // Calculate total amount
  const totalAmount = categories.length > 1 
    ? Object.values(categoryAmounts).reduce((sum, amount) => sum + (parseFloat(amount) || 0), 0)
    : parseFloat(amount) || 0;

  // Get car from URL params or current selection
  const carIdFromParams = searchParams.get('carId');
  const defaultCarId = carIdFromParams || currentCar?.id || '';

  useEffect(() => {
    if (defaultCarId && cars.find(c => c.id === defaultCarId)) {
      setSelectedCarId(defaultCarId);
    }
  }, [defaultCarId, cars]);

  // Fetch last odometer reading when car changes
  useEffect(() => {
    if (selectedCarId) {
      const fetchLastOdometer = async () => {
        try {
          const { data } = await supabase
            .from('odometer_readings')
            .select('reading')
            .eq('car_id', selectedCarId)
            // Latest = highest reading value (regardless of date); if equal, newest date wins
            .order('reading', { ascending: false })
            .order('recorded_at', { ascending: false })
            .limit(10); // Fetch top 10 to check for outliers
          
          if (data && data.length > 0) {
            const { filterOdometerOutliers } = await import('@/lib/utils');
            const validReading = filterOdometerOutliers(data);
            setLastOdometerReading(validReading);
          }
        } catch (error) {
          console.error('Error fetching last odometer reading:', error);
        }
      };
      
      fetchLastOdometer();
    }
  }, [selectedCarId]);

  // Set default category from URL params and detect navigation source
  useEffect(() => {
    const categoryFromParams = searchParams.get('category');
    
    // Simple approach: if there's no category param, it's a clean Add Expense
    if (!categoryFromParams) {
      setCategories([]);
      setIsFromBottomNav(false);
      
      // Reset form for clean Add Expense
      setAmount('');
      setCategoryAmounts({});
      setDescription('');
      setExpenseDate(new Date());
      setOdometerReading('');
      setFuelVolume('');
      setTankFullyFilled(false);
      setImageFiles([]);
      setError('');
    } else if (categoryFromParams === 'Refueling' && categoriesToUse.find(c => c.value === categoryFromParams)) {
      setCategories([categoryFromParams]);
      setIsFromBottomNav(true);
      
      // Reset all form fields when coming from bottom nav refueling
      setAmount('');
      setCategoryAmounts({});
      setDescription('');
      setExpenseDate(new Date());
      setOdometerReading('');
      setFuelVolume('');
      setTankFullyFilled(false);
      setImageFiles([]);
      setError('');
    } else if (categoryFromParams && categoriesToUse.find(c => c.value === categoryFromParams)) {
      setCategories([categoryFromParams]);
      setIsFromBottomNav(false);
    }
  }, [searchParams, categoriesToUse]);

  // Load expense for editing
  useEffect(() => {
    if (!expenseId) {
      setIsEditing(false);
      return;
    }

    const loadExpense = async () => {
      try {
        const { data, error } = await supabase
          .from('expenses')
          .select('*')
          .eq('id', expenseId)
          .is('deleted_at', null)
          .single();

        if (error) throw error;
        if (!data) return;

        setIsEditing(true);
        setSelectedCarId(data.car_id);
        setCategories([data.category]);
        setAmount((data.amount_cents / 100).toString());
        setDescription(data.description || '');
        setExpenseDate(new Date(data.expense_date));
        setOdometerReading(data.odometer_reading ? data.odometer_reading.toString() : '');
        
        // Load existing receipt image if available
        if (data.receipt_url) {
          setExistingReceiptPath(data.receipt_url);
          try {
            const signedUrlInfo = await getSignedViewUrl(data.receipt_url);
            setExistingReceiptUrl(signedUrlInfo.url);
          } catch (error) {
            console.error('Error loading existing receipt:', error);
            setExistingReceiptUrl(null);
            setExistingReceiptPath(null);
          }
        } else {
          setExistingReceiptUrl(null);
          setExistingReceiptPath(null);
        }

        // Load refueling-specific fields
        if (data.category === 'Refueling') {
          setFuelVolume(data.fuel_volume_liters ? data.fuel_volume_liters.toString() : '');
          setTankFullyFilled(data.tank_fully_filled || false);
        }
      } catch (error) {
        console.error('Error loading expense:', error);
        toast({
          title: t('common.error') || 'Error',
          description: t('common.loadFailed') || 'Failed to load expense',
          variant: 'destructive',
        });
      }
    };

    loadExpense();
  }, [expenseId, t]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCarId || categories.length === 0) {
      setError(t('addCost.errors.fillRequiredFields'));
      return;
    }

    // Validate amounts based on category selection
    if (categories.length === 1) {
      if (!amount) {
        setError(t('addCost.errors.enterAmount'));
        return;
      }
      const amountCents = Math.round(parseFloat(amount) * 100);
      if (isNaN(amountCents) || amountCents <= 0) {
        setError(t('addCost.errors.validAmount'));
        return;
      }
    } else {
      // Multiple categories - validate each category amount
      for (const category of categories) {
        const categoryAmount = categoryAmounts[category];
        if (!categoryAmount) {
          const categoryLabel = t(`category.${category}`) || category;
          setError(t('addCost.errors.enterAmountForCategory', { category: categoryLabel }));
          return;
        }
        const amountCents = Math.round(parseFloat(categoryAmount) * 100);
        if (isNaN(amountCents) || amountCents <= 0) {
          const categoryLabel = t(`category.${category}`) || category;
          setError(t('addCost.errors.validAmountForCategory', { category: categoryLabel }));
          return;
        }
      }
    }

    // Validate refueling-specific fields
    if (categories.includes('Refueling')) {
      if (fuelVolume && (isNaN(parseFloat(fuelVolume)) || parseFloat(fuelVolume) <= 0)) {
        setError(t('addCost.errors.validFuelVolume'));
        return;
      }
    }

    // Validate recurring rules where set: need at least repeat; start/end default from form date
    for (const cat of categories) {
      const lineKey = categories.length <= 1 ? '_single' : cat;
      const rec = recurringByLine[lineKey];
      if (rec?.repeat) {
        const start = rec.startDate?.trim() || expenseDate.toISOString().slice(0, 10);
        const end = rec.endDate?.trim() || (() => {
          const d = new Date(start);
          d.setFullYear(d.getFullYear() + 10);
          return d.toISOString().slice(0, 10);
        })();
        if (end < start) {
          setError(t('addCost.errors.recurringEndBeforeStart') || 'Recurring end date must be on or after start date.');
          return;
        }
      }
    }

    setSubmitting(true);
    setError('');

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Handle receipt image upload
      let receiptUrl = existingReceiptPath; // Use original path, not signed URL
      if (imageFiles.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('No session');
          
          // Upload first image as receipt
          receiptUrl = await uploadCarImage({
            file: imageFiles[0],
            userId: user.id,
            entityId: expenseId || selectedCarId,
            // Use existing allowed type key; receipts are stored under 'repair' bucket
            type: 'repair',
            accessToken: session.access_token,
          });
        } catch (uploadError) {
          console.error('Error uploading receipt:', uploadError);
          throw new Error('Failed to upload receipt image');
        }
      } else if (isEditing && !existingReceiptPath) {
        // If editing and existing receipt was removed, set to null
        receiptUrl = null;
      }

      if (isEditing && expenseId) {
        // Update existing expense
        const amountToUse = categories.length === 1 
          ? parseFloat(amount) 
          : parseFloat(categoryAmounts[categories[0]] || '0');
        
        const updateRecord: any = {
          category: categories[0],
          amount_cents: Math.round(amountToUse * 100),
          description: description || null,
          expense_date: expenseDate.toISOString(),
          odometer_reading: odometerReading ? parseInt(odometerReading) : null,
          receipt_url: receiptUrl,
        };

        // Add refueling-specific fields if this is a refueling expense
        if (categories[0] === 'Refueling') {
          updateRecord.fuel_volume_liters = fuelVolume ? parseFloat(fuelVolume) : null;
          updateRecord.tank_fully_filled = tankFullyFilled;
        }

        const { error: updateError } = await supabase
          .from('expenses')
          .update(updateRecord)
          .eq('id', expenseId);

        if (updateError) {
          throw updateError;
        }

        // Update or insert odometer reading for all expenses
        // Always check, even if odometer is cleared (to handle deletion)
        const trimmedOdometer = odometerReading?.trim() || '';
        const parsedReading = trimmedOdometer ? parseInt(trimmedOdometer, 10) : NaN;
        // Use 'fuel_log' for refueling, 'manual' for other expenses (database constraint only allows: 'fuel_log', 'service', 'manual', 'fluid_log')
        const sourceValue = categories[0] === 'Refueling' ? 'fuel_log' : 'manual';
        
        // Find any existing odometer_readings linked to this expense
        // Check both 'fuel_log' and 'manual' sources in case category was changed
        const { data: existingOdo, error: existingOdoError } = await supabase
          .from('odometer_readings')
          .select('id')
          .eq('car_id', selectedCarId)
          .in('source', ['fuel_log', 'manual'])
          .eq('related_id', expenseId)
          .maybeSingle();

        if (existingOdoError && existingOdoError.code !== 'PGRST116') {
          console.error('Error loading existing odometer reading for expense:', existingOdoError);
        }

        if (!isNaN(parsedReading) && parsedReading > 0) {
          // Convert to km if user is using miles
          const readingKm = settings?.distanceUnit === 'mi'
            ? Math.round(parsedReading * 1.609344)
            : parsedReading;

          if (existingOdo?.id) {
            // Update existing reading
            const { error: odoUpdateError } = await supabase
              .from('odometer_readings')
              .update({
                reading: readingKm,
                recorded_at: expenseDate.toISOString(),
                source: sourceValue, // Update source in case category changed
              })
              .eq('id', existingOdo.id);

            if (odoUpdateError) {
              console.error('Failed to update odometer reading for expense:', odoUpdateError);
              throw new Error(`Failed to update odometer reading: ${odoUpdateError.message}`);
            }
          } else {
            // Insert new reading
            const { error: odoInsertError } = await supabase.from('odometer_readings').insert({
              car_id: selectedCarId,
              entered_by: user.id,
              reading: readingKm,
              recorded_at: expenseDate.toISOString(),
              source: sourceValue,
              related_id: expenseId,
            });

            if (odoInsertError) {
              console.error('Failed to insert odometer reading for expense:', odoInsertError);
              throw new Error(`Failed to insert odometer reading: ${odoInsertError.message}`);
            }
          }
        } else if (existingOdo?.id && !trimmedOdometer) {
          // If odometer was cleared, remove the linked reading
          const { error: odoDeleteError } = await supabase
            .from('odometer_readings')
            .delete()
            .eq('id', existingOdo.id);

          if (odoDeleteError) {
            console.error('Failed to delete odometer reading for expense:', odoDeleteError);
            // Don't throw for delete errors - it's not critical
          }
        }

        toast({
          title: t('common.success') || 'Success',
          description: t('common.updated') || 'Expense updated successfully',
        });

        // Navigate back to expense view or list
        navigate(`/expenses/view/${expenseId}?carId=${selectedCarId}`);
      } else {
        // Split categories: recurring (rule + backfill past via materialize) vs one-time (single expense row)
        const isRecurring = (cat: string) => {
          const lineKey = categories.length <= 1 ? '_single' : cat;
          return Boolean(recurringByLine[lineKey]?.repeat);
        };
        const oneTimeCategories = categories.filter(cat => !isRecurring(cat));
        const recurringCategories = categories.filter(cat => isRecurring(cat));

        // 1) Insert one expense record per one-time category (same date = form date)
        const expenseRecords = oneTimeCategories.map(cat => {
          const amountToUse = categories.length === 1
            ? parseFloat(amount)
            : parseFloat(categoryAmounts[cat] || '0');

          const baseRecord = {
            car_id: selectedCarId,
            user_id: user.id,
            category: cat,
            amount_cents: Math.round(amountToUse * 100),
            currency_code: currency,
            description: description || null,
            expense_date: expenseDate.toISOString(),
            odometer_reading: odometerReading ? parseInt(odometerReading) : null,
            receipt_url: receiptUrl,
          };

          if (cat === 'Refueling') {
            return {
              ...baseRecord,
              fuel_volume_liters: fuelVolume ? parseFloat(fuelVolume) : null,
              tank_fully_filled: tankFullyFilled,
            };
          }
          return baseRecord;
        });

        if (expenseRecords.length > 0) {
          const { data: insertedExpenses, error: insertError } = await supabase
            .from('expenses')
            .insert(expenseRecords)
            .select('id, category, odometer_reading');

          if (insertError) {
            throw insertError;
          }

          // Insert odometer readings for one-time expenses that have odometer
          const trimmedOdometer = odometerReading?.trim() || '';
          if (insertedExpenses && trimmedOdometer) {
            const expensesWithOdometer = insertedExpenses.filter(
              (expense) => expense.odometer_reading
            );
            for (const expense of expensesWithOdometer) {
              const parsedReading = parseInt(trimmedOdometer, 10);
              if (!isNaN(parsedReading) && parsedReading > 0) {
                const readingKm = settings?.distanceUnit === 'mi'
                  ? Math.round(parsedReading * 1.609344)
                  : parsedReading;
                const { error: odoInsertError } = await supabase.from('odometer_readings').insert({
                  car_id: selectedCarId,
                  entered_by: user.id,
                  reading: readingKm,
                  recorded_at: expenseDate.toISOString(),
                  source: expense.category === 'Refueling' ? 'fuel_log' : 'manual',
                  related_id: expense.id,
                });
                if (odoInsertError) {
                  console.error('Failed to insert odometer reading for expense:', odoInsertError);
                  throw new Error(`Failed to insert odometer reading: ${odoInsertError.message}`);
                }
              }
            }
          }
        }

        // 2) Upload recurring documents (one per line), then insert recurring_expense_rules
        const recurringReceiptUrls: Record<string, string> = {};
        const recurringLinesToUpload = recurringCategories.filter(cat => {
          const lineKey = categories.length <= 1 ? '_single' : cat;
          return Boolean(recurringReceiptByLine[lineKey]);
        });
        if (recurringLinesToUpload.length > 0) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) throw new Error('No session');
          for (const cat of recurringLinesToUpload) {
            const lineKey = categories.length <= 1 ? '_single' : cat;
            const file = recurringReceiptByLine[lineKey];
            if (!file) continue;
            try {
              const path = await uploadCarImage({
                file,
                userId: user.id,
                entityId: selectedCarId,
                type: 'repair',
                accessToken: session.access_token,
              });
              recurringReceiptUrls[lineKey] = path;
            } catch (uploadErr) {
              console.error('Failed to upload recurring document:', uploadErr);
              throw new Error('Failed to upload document for recurring expense');
            }
          }
        }

        for (const cat of recurringCategories) {
          const lineKey = categories.length <= 1 ? '_single' : cat;
          const rec = recurringByLine[lineKey];
          if (!rec?.repeat) continue;

          const amountToUse = categories.length === 1
            ? parseFloat(amount)
            : parseFloat(categoryAmounts[cat] || '0');
          const amountCents = Math.round(amountToUse * 100);
          if (isNaN(amountCents) || amountCents <= 0) continue;

          const startDate = rec.startDate?.trim() || expenseDate.toISOString().slice(0, 10);
          let endDate = rec.endDate?.trim();
          if (!endDate) {
            const d = new Date(startDate);
            d.setFullYear(d.getFullYear() + 10);
            endDate = d.toISOString().slice(0, 10);
          }

          const { error: ruleError } = await supabase.from('recurring_expense_rules').insert({
            car_id: selectedCarId,
            user_id: user.id,
            category: cat,
            amount_cents: amountCents,
            currency_code: currency,
            description: description || null,
            start_date: startDate,
            end_date: endDate,
            repeat_interval: rec.repeat,
            receipt_url: recurringReceiptUrls[lineKey] ?? null,
          });
          if (ruleError) {
            console.error('Failed to insert recurring rule:', ruleError);
            throw new Error(`Failed to save recurring expense rule: ${ruleError.message}`);
          }
        }

        // 3) Backfill historical expense rows for all recurring rules (past + today); future dates stay scheduled
        if (recurringCategories.length > 0) {
          const { error: materializeError } = await supabase.rpc('materialize_recurring_expenses');
          if (materializeError) {
            console.error('Failed to materialize recurring expenses:', materializeError);
            throw new Error(`Failed to create historical recurring expenses: ${materializeError.message}`);
          }
        }

        if (categories.includes('Insurance')) {
          setShowInsuranceDetailsPrompt(true);
        } else {
          navigate(`/cars/${selectedCarId}?tab=costs`);
        }
      }
    } catch (err) {
      console.error('Error adding expense:', err);
      setError('Failed to add expense. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedCar = cars.find(c => c.id === selectedCarId);

  // Show loading state while categories are being fetched
  if (categoriesLoading) {
    return (
      <PageLayout title={t('addCost.title') || 'Add Expense'} showBackButton onBack={() => navigate(-1)}>
        <div className="max-w-5xl mx-auto px-1 py-2">
          <div className="flex items-center justify-center py-8">
            <div className="text-sm text-gray-500">{t('addCost.loadingCategories') || 'Loading categories...'}</div>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <>
    <PageLayout 
      title={isEditing ? (t('addCost.editTitle') || 'Edit Expense') : t('addCost.title')} 
      showBackButton 
      onBack={() => navigate(-1)}
    >
      <div className="max-w-5xl mx-auto px-1 py-2">
        <form onSubmit={handleSubmit} className="pb-32 space-y-3" aria-describedby={error ? 'form-error' : undefined}>
          {/* Car/Date/Odometer Section */}
          <div className="rounded-2xl bg-white shadow-md p-3 sm:p-4 space-y-3">
            
            {/* Car selection - full width */}
            <div>
              <Label htmlFor="car" className="text-sm text-gray-600">{t('addCost.carRequired')}</Label>
              <Select value={selectedCarId} onValueChange={setSelectedCarId}>
                <SelectTrigger className="h-10 rounded-xl border border-gray-300">
                  <SelectValue placeholder={t('addCost.selectCar')} />
                </SelectTrigger>
                <SelectContent>
                  {cars.map((car) => (
                    <SelectItem key={car.id} value={car.id}>
                      <span>{car.nickname || `${car.brand} ${car.model}`}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date + Odometer row */}
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <Label htmlFor="date" className="text-sm text-gray-600">{t('addCost.dateRequired')}</Label>
                </div>
                <div className="relative">
                  <Input
                    id="date"
                    type="date"
                    value={(() => {
                      try {
                        return new Date(expenseDate).toISOString().slice(0,10);
                      } catch { return new Date().toISOString().slice(0,10); }
                    })()}
                    onChange={(e) => setExpenseDate(new Date(e.target.value))}
                    className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm"
                    style={{
                      colorScheme: 'light',
                      color: 'transparent',
                      boxShadow: 'rgba(12, 12, 13, 0.05) 0px 4px 4px -1px'
                    }}
                    required
                  />
                  <div className="absolute inset-0 flex items-center px-3 pointer-events-none text-sm text-gray-700">
                    {formatDateI18n(expenseDate, 'dd-MMM-yyyy')}
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <div className="flex items-center gap-1 mb-1">
                  <Label htmlFor="odometer" className="text-sm text-gray-600">{t('addCost.odometer')}</Label>
                  <button
                    type="button"
                    onClick={() => setShowOdometerInfo(true)}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                  >
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </div>
                <Input
                  id="odometer"
                  type="number"
                  min="0"
                  value={odometerReading}
                  onChange={(e) => setOdometerReading(e.target.value)}
                  placeholder={lastOdometerReading ? t('addCost.lastOdometer', { reading: lastOdometerReading.toLocaleString() }) : t('addCost.optional')}
                  className="h-10 rounded-xl border border-gray-300 bg-white px-3 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
                {/* Removed helper text; info icon explains purpose */}
              </div>
            </div>
          </div>

          {/* Categories & Amounts Section */}
          <div className="rounded-2xl bg-white shadow-md p-3 sm:p-4 space-y-3">
            
            {/* Categories */}
            <div>
              <Label htmlFor="categories" className="text-sm text-gray-600">{t('addCost.categoriesRequired')}</Label>
              <div className="mt-2">
                <CategoryMultiSelect value={categories} onChange={handleCategoriesChange} categories={categoriesToUse} />
              </div>
              
              {/* Selected category labels - only show for multiple categories */}
              {categories.length > 1 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {categories.map((c) => {
                    const cat = categoriesToUse.find((x) => x.value === c);
                    return (
                      <span key={c} className="inline-flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-xs text-gray-800">
                        {cat?.value !== 'Other' && cat?.icon_path && <img src={cat.icon_path} alt={t(`category.${c}`)} className="w-4 h-4" style={{ filter: "grayscale(1)", opacity: 0.8 }} />}
                        {cat ? (t(`category.${cat.value}`) || cat.label) : c}
                      </span>
                    );
                  })}
                </div>
              )}
              
              {/* Helper text - only show when no categories selected */}
              {categories.length === 0 && (
                <p className="mt-1 text-[0.75rem] text-[#868686]">{t('addCost.multipleCategoriesHint')}</p>
              )}
              
              {/* Show additional categories hint only when Refueling is pre-selected from bottom nav */}
              {categories.includes('Refueling') && isFromBottomNav && (
                <p className="mt-1 text-[0.75rem] text-[#868686]">{t('addCost.multipleCategoriesHint')}</p>
              )}
            </div>

            {/* Amount fields */}
            {categories.length === 0 ? (
              // No categories selected - show generic amount field
              <div className="flex items-center gap-3">
                <Label htmlFor="amount" className="text-sm text-gray-600 flex-1">{t('addCost.amountRequired')}</Label>
                <div className="flex items-center gap-2">
                  <div className="relative w-28">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={amount}
                      onChange={(e) => setAmount(normalizeDecimalInput(e.target.value))}
                      placeholder="0.00"
                      className="h-10 rounded-xl border border-gray-300 bg-white pl-9 pr-2 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setRecurringDrawerFor('_single'); setRecurringDrawerOpen(true); }}
                    className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    aria-label={t('addCost.ariaLabels.recurringExpense') || t('addCost.recurringTitle')}
                  >
                    <CalendarSync className={cn('w-5 h-5', recurringByLine['_single']?.repeat && 'text-[#7097fa]')} />
                  </button>
                </div>
              </div>
            ) : categories.length === 1 ? (
              // Single category - simple amount field
              <div className="flex items-center gap-3">
                <Label htmlFor="amount" className="text-sm text-gray-600 flex-1">{t('addCost.amountRequired')}</Label>
                <div className="flex items-center gap-2">
                  <div className="relative w-28">
                    <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={amount}
                      onChange={(e) => setAmount(normalizeDecimalInput(e.target.value))}
                      placeholder="0.00"
                      className="h-10 rounded-xl border border-gray-300 bg-white pl-9 pr-2 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      required
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setRecurringDrawerFor('_single'); setRecurringDrawerOpen(true); }}
                    className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                    aria-label={t('addCost.ariaLabels.recurringExpense') || t('addCost.recurringTitle')}
                  >
                    <CalendarSync className={cn('w-5 h-5', recurringByLine['_single']?.repeat && 'text-[#7097fa]')} />
                  </button>
                </div>
              </div>
            ) : (
              // Multiple categories - separate amount fields
              <div className="space-y-2">
                {categories.map((category) => {
                  const cat = categoriesToUse.find(c => c.value === category);
                  const lineRecurring = recurringByLine[category]?.repeat;
                  return (
                    <div key={category} className="flex items-center gap-3">
                      <Label className="text-sm text-gray-600 flex-1 flex items-center gap-2">
                        {cat?.value !== 'Other' && cat?.icon_path && (
                          <img
                            src={cat.icon_path}
                            alt={t(`category.${category}`)}
                            className={cn('w-4 h-4', lineRecurring && 'opacity-100')}
                            style={lineRecurring ? { filter: 'brightness(0) saturate(100%) invert(52%) sepia(78%) saturate(1200%) hue-rotate(210deg)' } : undefined}
                          />
                        )}
                        {cat ? (t(`category.${cat.value}`) || cat.label) : category}
                      </Label>
                      <div className="flex items-center gap-2">
                        <div className="relative w-28">
                          <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={categoryAmounts[category] || ''}
                            onChange={(e) => handleCategoryAmountChange(category, e.target.value)}
                            placeholder="0.00"
                            className="h-10 rounded-xl border border-gray-300 bg-white pl-9 pr-2 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                            required
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => { setRecurringDrawerFor(category); setRecurringDrawerOpen(true); }}
                          className="shrink-0 p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                          aria-label={t('addCost.ariaLabels.recurringExpense') || t('addCost.recurringTitle')}
                        >
                          <CalendarSync className={cn('w-5 h-5', lineRecurring && 'text-[#7097fa]')} />
                        </button>
                      </div>
                    </div>
                  );
                })}
                {/* Total sum */}
                <div className="flex items-center gap-3 pt-2 border-t border-gray-200">
                  <Label className="text-sm font-semibold text-gray-800 flex-1">{t('addCost.total')}</Label>
                  <div className="flex items-center gap-2">
                    <div className="relative w-28">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <div className="h-10 rounded-xl border border-gray-300 bg-gray-50 pl-9 pr-2 flex items-center text-sm font-semibold text-gray-900">
                        {formatCurrency(totalAmount)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Validation hint for zero total */}
                {totalAmount === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {t('addCost.errors.enterPositiveAmount')}
                  </p>
                )}
              </div>
            )}

            {/* Refueling-specific fields */}
            {categories.includes('Refueling') && (
              <div className="space-y-3 pt-3 border-t border-gray-200">
                {/* Fuel Volume */}
                <div className="flex items-center gap-3">
                  <Label htmlFor="fuelVolume" className="text-sm text-gray-600 flex-1">{t('addCost.fuelVolume')}</Label>
                  <div className="relative w-1/2">
                    <Droplets className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                    <Input
                      id="fuelVolume"
                      type="number"
                      step="0.001"
                      min="0"
                      value={fuelVolume}
                      onChange={(e) => setFuelVolume(normalizeFuelVolumeInput(e.target.value))}
                      placeholder="0.000"
                      className="h-10 rounded-xl border border-gray-300 bg-white pl-9 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    />
                  </div>
                </div>

                {/* Tank fully filled label and checkbox in same row */}
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 flex-1">
                    <Label htmlFor="tankFullyFilled" className="text-sm text-gray-600">{t('addCost.tankFullyFilled')}</Label>
                    <button
                      type="button"
                      onClick={() => setShowTankFilledInfo(true)}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-0.5"
                      aria-label={t('addCost.ariaLabels.tankFilledInfo') || 'More info about full tank'}
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="w-1/2">
                    <button
                      type="button"
                      onClick={() => setTankFullyFilled(!tankFullyFilled)}
                      className="inline-flex items-center justify-center w-5 h-5 rounded"
                      style={{
                        backgroundColor: tankFullyFilled ? '#7c9ffa' : 'transparent',
                        border: '1px solid rgb(209, 213, 219)',
                        borderRadius: '0.375rem',
                        boxShadow: 'rgba(12, 12, 13, 0.05) 0px 4px 4px -1px'
                      }}
                      aria-label={t('addCost.ariaLabels.toggleTankFilled') || 'Toggle tank fully filled'}
                    >
                      {tankFullyFilled && <Check className="w-3.5 h-3.5 text-white" />}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Receipt Photos & Notes Section */}
          <div className="rounded-2xl bg-white shadow-md p-3 sm:p-4 space-y-4">
            {/* Receipt Photos */}
            <div>
              <Label className="text-sm text-gray-600">
                {t('addCost.receiptPhotos')} {imageFiles.length > 0 && `(${t('addCost.receiptPhotosCount', { count: imageFiles.length })})`}
              </Label>
              <div className="flex gap-4 mt-2 justify-center">
                {/* Show existing receipt if editing and no new images */}
                {isEditing && existingReceiptUrl && imageFiles.length === 0 && (
                  <div 
                    className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer shadow-sm border border-gray-200 relative" 
                    onClick={() => {
                      setCurrentImageIndex(-1);
                      setFullScreenImage(existingReceiptUrl);
                      setImageZoom(1);
                      setImageOffset({ x: 0, y: 0 });
                    }}
                  >
                    <img
                      src={existingReceiptUrl}
                      alt={t('addCost.ariaLabels.receipt') || 'Receipt'}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => { 
                        e.stopPropagation(); 
                        setExistingReceiptUrl(null);
                        setExistingReceiptPath(null);
                      }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-black/70 text-white rounded-full flex items-center justify-center text-xs hover:bg-black/80"
                    >
                      ×
                    </button>
                  </div>
                )}
                {imageFiles.slice(0, 3).map((file, idx) => (
                  <div 
                    key={idx} 
                    className="w-20 h-20 rounded-lg overflow-hidden cursor-pointer shadow-sm border border-gray-200 relative" 
                    onClick={() => {
                      setCurrentImageIndex(idx);
                      setFullScreenImage(URL.createObjectURL(file));
                      setImageZoom(1);
                      setImageOffset({ x: 0, y: 0 });
                    }}
                  >
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`receipt-${idx}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); handleRemoveImage(idx); }}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-black/70 text-white rounded-full flex items-center justify-center text-xs hover:bg-black/80"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {(imageFiles.length + (isEditing && existingReceiptUrl && imageFiles.length === 0 ? 1 : 0)) < 3 && (
                  <div
                    className="w-20 h-20 bg-[#FDFDFD] rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-100 transition-colors"
                    style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}
                    onClick={handleAddImageClick}
                  >
                    <div className="w-8 h-8 flex items-center justify-center">
                      <img src="/icons/AddCarImage.png" alt={t('addCost.ariaLabels.add') || 'Add'} className="w-7 h-7 object-contain" />
                    </div>
                  </div>
                )}
                <input
                  type="file"
                  accept="image/*"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={handleImageInputChange}
                  disabled={imageFiles.length >= 3}
                  multiple={imageFiles.length < 3}
                />
              </div>
            </div>

            {/* Recurring expense document (one per recurring line; stored once, linked to each materialized expense) */}
            {(() => {
              const isRecurring = (cat: string) => {
                const key = categories.length <= 1 ? '_single' : cat;
                return Boolean(recurringByLine[key]?.repeat);
              };
              const recurringLines = categories.length <= 1
                ? (recurringByLine['_single']?.repeat ? [{ lineKey: '_single', label: categories[0] ? (t(`category.${categories[0]}`) || categories[0]) : t('addCost.expense') }] : [])
                : categories.filter(isRecurring).map(cat => ({ lineKey: cat, label: t(`category.${cat}`) || cat }));
              if (recurringLines.length === 0) return null;
              return (
                <div className="space-y-3 pt-3 border-t border-gray-200">
                  <Label className="text-sm text-gray-600">{t('addCost.recurringDocument') || 'Document for recurring expense'}</Label>
                  <p className="text-xs text-gray-500">{t('addCost.recurringDocumentHint') || 'Upload once (e.g. contract); it will be linked to each expense entry for this recurring line.'}</p>
                  <div className="flex flex-wrap gap-3">
                    {recurringLines.map(({ lineKey, label }) => {
                      const file = recurringReceiptByLine[lineKey];
                      return (
                        <div key={lineKey} className="flex items-center gap-2">
                          <span className="text-xs text-gray-600 shrink-0">{label}:</span>
                          {file ? (
                            <div className="flex items-center gap-1.5">
                              <div className="w-14 h-14 rounded-lg overflow-hidden border border-gray-200">
                                <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />
                              </div>
                              <button
                                type="button"
                                onClick={() => removeRecurringReceipt(lineKey)}
                                className="text-xs text-gray-500 hover:text-red-600"
                              >
                                {t('actions.remove') || 'Remove'}
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => openRecurringFilePicker(lineKey)}
                              className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-700 hover:bg-gray-50"
                            >
                              <img src="/icons/AddCarImage.png" alt="" className="w-4 h-4" />
                              {t('addCost.addDocument') || 'Add document'}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    ref={recurringFileInputRef}
                    className="hidden"
                    onChange={handleRecurringFileChange}
                  />
                </div>
              );
            })()}

            {/* Notes */}
            <div>
              <Label htmlFor="description" className="text-sm text-gray-600">{t('addCost.notes')}</Label>
              <div className="relative mt-2">
                <Textarea
                  id="description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('addCost.addNotes')}
                  className="rounded-xl border border-gray-300 bg-white px-3 text-sm"
                  style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {error && (
            <div id="form-error" className="bg-red-50 border border-red-200 rounded-xl p-3">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          {/* Sticky footer actions */}
          <div className="fixed left-0 right-0 bottom-16 z-50 border-t border-gray-200 bg-white">
            <div className="mx-auto max-w-5xl px-1 py-2 flex gap-2">
              <Button type="button" variant="secondary" onClick={() => navigate(-1)} className="flex-1 rounded-xl border border-gray-300">{t('addCost.cancel')}</Button>
              <Button type="submit" variant="default" disabled={submitting || !selectedCarId || categories.length === 0 || (categories.length === 1 ? !amount : Object.keys(categoryAmounts).length !== categories.length)} className="flex-1 rounded-xl bg-[#7c9ffa] hover:bg-[#6b8ce8]">
                {submitting ? (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 animate-spin" />
                    {isEditing ? (t('addCost.updating') || 'Updating...') : t('addCost.adding')}
                  </div>
                ) : (
                  isEditing ? (t('addCost.updateExpense') || 'Update Expense') : t('addCost.addExpense')
                )}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </PageLayout>

    {/* Full Screen Image Modal */}
    {fullScreenImage && (
      <div 
        className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
        onClick={() => {
          if (imageZoom === 1) {
            setFullScreenImage(null);
            setCurrentImageIndex(-1);
            setImageZoom(1);
            setImageOffset({ x: 0, y: 0 });
          }
        }}
      >
        <button
          onClick={() => {
            setFullScreenImage(null);
            setCurrentImageIndex(-1);
            setImageZoom(1);
            setImageOffset({ x: 0, y: 0 });
          }}
          className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-10"
          aria-label={t('addCost.ariaLabels.close') || 'Close'}
        >
          <X className="w-8 h-8" />
        </button>

        {/* Previous Button */}
        {imageFiles.length > 1 && currentImageIndex > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newIndex = currentImageIndex - 1;
              setCurrentImageIndex(newIndex);
              setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }}
            className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
            aria-label={t('addCost.ariaLabels.previousImage') || 'Previous image'}
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
        )}

        {/* Next Button */}
        {imageFiles.length > 1 && currentImageIndex < imageFiles.length - 1 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              const newIndex = currentImageIndex + 1;
              setCurrentImageIndex(newIndex);
              setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
              setImageZoom(1);
              setImageOffset({ x: 0, y: 0 });
            }}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 transition-colors z-10 bg-black/50 rounded-full p-2"
            aria-label={t('addCost.ariaLabels.nextImage') || 'Next image'}
          >
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Image Counter */}
        {imageFiles.length > 1 && (
          <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full z-10">
            {currentImageIndex + 1} / {imageFiles.length}
          </div>
        )}

        {/* Image Container with Zoom and Pan */}
        <div
          className="relative w-full h-full flex items-center justify-center touch-none"
          onWheel={(e) => {
            if (e.ctrlKey || e.metaKey) {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.1 : 0.1;
              setImageZoom(prev => Math.max(1, Math.min(5, prev + delta)));
            }
          }}
          onMouseDown={(e) => {
            if (imageZoom > 1 && e.button === 0) {
              panStartRef.current = { x: e.clientX - imageOffset.x, y: e.clientY - imageOffset.y };
            }
          }}
          onMouseMove={(e) => {
            if (panStartRef.current && imageZoom > 1) {
              setImageOffset({
                x: e.clientX - panStartRef.current.x,
                y: e.clientY - panStartRef.current.y,
              });
            }
          }}
          onMouseUp={() => {
            panStartRef.current = null;
          }}
          onMouseLeave={() => {
            panStartRef.current = null;
          }}
          onTouchStart={(e) => {
            if (e.touches.length === 1 && imageZoom === 1) {
              const touch = e.touches[0];
              panStartRef.current = { x: touch.clientX, y: touch.clientY };
            }
          }}
          onTouchMove={(e) => {
            if (e.touches.length === 1 && imageZoom === 1 && panStartRef.current) {
              const touch = e.touches[0];
              const deltaX = touch.clientX - panStartRef.current.x;
              if (Math.abs(deltaX) > 50) {
                if (deltaX > 0 && currentImageIndex > 0) {
                  const newIndex = currentImageIndex - 1;
                  setCurrentImageIndex(newIndex);
                  setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
                  panStartRef.current = null;
                } else if (deltaX < 0 && currentImageIndex < imageFiles.length - 1) {
                  const newIndex = currentImageIndex + 1;
                  setCurrentImageIndex(newIndex);
                  setFullScreenImage(URL.createObjectURL(imageFiles[newIndex]));
                  panStartRef.current = null;
                }
              }
            }
          }}
          onTouchEnd={() => {
            panStartRef.current = null;
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <img
            src={fullScreenImage}
            alt={t('addCost.ariaLabels.fullScreen') || 'Full screen'}
            className="max-w-full max-h-full object-contain select-none"
            style={{
              transform: imageZoom > 1 
                ? `translate(${imageOffset.x}px, ${imageOffset.y}px) scale(${imageZoom})`
                : 'scale(1)',
              transformOrigin: 'center center',
              cursor: imageZoom > 1 ? 'grab' : 'default',
              transition: imageZoom === 1 ? 'transform 0.2s' : 'none',
            }}
            draggable={false}
          />
        </div>

        {/* Zoom Indicator */}
        {imageZoom > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-xs bg-black/50 px-3 py-1 rounded-full z-10">
            {Math.round(imageZoom * 100)}%
          </div>
        )}
      </div>
    )}

    {/* Odometer Info Dialog */}
    <Dialog open={showOdometerInfo} onOpenChange={setShowOdometerInfo}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('addCost.odometerInfo.title')}</DialogTitle>
          <DialogDescription>
            {t('addCost.odometerInfo.description')}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>

    {/* Tank Filled Info Dialog */}
    <Dialog open={showTankFilledInfo} onOpenChange={setShowTankFilledInfo}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('addCost.tankFilledInfo.title')}</DialogTitle>
          <DialogDescription>
            {t('addCost.tankFilledInfo.description')}
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>

    {/* Recurring expense bottom drawer – draft recurrence; Apply commits to the line */}
    <Sheet open={recurringDrawerOpen} onOpenChange={(open) => { if (!open) handleRecurringDrawerClose(); }}>
      <SheetContent side="bottom" className="h-auto max-h-[90vh] rounded-t-2xl p-6 border-t border-gray-200">
        <SheetHeader>
          <SheetTitle className="text-lg font-semibold text-black text-center" style={{ fontFamily: 'Lato', fontWeight: 700, fontSize: '18px', lineHeight: '24px' }}>
            {t('addCost.recurringTitle') || 'Recurring expense'}
          </SheetTitle>
        </SheetHeader>
        {recurringDrawerFor !== null && (
          <>
            <div className="mt-6 space-y-4">
              <div>
                <Label htmlFor="recurring-starts" className="text-sm text-gray-600">{t('addCost.recurringStarts') || 'Starts'}</Label>
                <Input
                  id="recurring-starts"
                  type="date"
                  value={recurringDrawerDraft.startDate}
                  onChange={(e) => setRecurringDrawerDraft(prev => ({ ...prev, startDate: e.target.value }))}
                  className="mt-1.5 h-10 rounded-xl border border-gray-300 bg-white text-sm"
                />
              </div>
              <div>
                <Label htmlFor="recurring-ends" className="text-sm text-gray-600">{t('addCost.recurringEnds') || 'Ends'}</Label>
                <Input
                  id="recurring-ends"
                  type="date"
                  value={recurringDrawerDraft.endDate}
                  onChange={(e) => setRecurringDrawerDraft(prev => ({ ...prev, endDate: e.target.value }))}
                  className="mt-1.5 h-10 rounded-xl border border-gray-300 bg-white text-sm"
                />
              </div>
              <div>
                <Label className="text-sm text-gray-600">{t('addCost.recurringRepeat') || 'Repeat'}</Label>
                <Select
                  value={recurringDrawerDraft.repeat || undefined}
                  onValueChange={(value) => setRecurringDrawerDraft(prev => ({ ...prev, repeat: value }))}
                >
                  <SelectTrigger id="recurring-repeat" className="mt-1.5 h-10 rounded-xl border border-gray-300 bg-white text-sm">
                    <SelectValue placeholder={t('addCost.recurringRepeat') || 'Repeat'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">{t('addCost.recurringRepeatOptions.everyDay') || 'Every day'}</SelectItem>
                    <SelectItem value="weekly">{t('addCost.recurringRepeatOptions.everyWeek') || 'Every week'}</SelectItem>
                    <SelectItem value="biweekly">{t('addCost.recurringRepeatOptions.every2Weeks') || 'Every 2 weeks'}</SelectItem>
                    <SelectItem value="monthly">{t('addCost.recurringRepeatOptions.everyMonth') || 'Every month'}</SelectItem>
                    <SelectItem value="quarterly">{t('addCost.recurringRepeatOptions.everyQuarter') || 'Every quarter'}</SelectItem>
                    <SelectItem value="yearly">{t('addCost.recurringRepeatOptions.everyYear') || 'Every year'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-6 pt-4 border-t border-gray-200">
              <Button variant="secondary" onClick={handleRecurringDrawerClose}>
                {t('addCost.recurringDrawer.close') || 'Close'}
              </Button>
              <Button onClick={handleRecurringDrawerApply}>
                {t('addCost.recurringDrawer.apply') || 'Apply'}
              </Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>

    {/* After adding Insurance expense: prompt to check/update insurance details (OCTA & KASKO) */}
    <Dialog open={showInsuranceDetailsPrompt} onOpenChange={(open) => { if (!open) { setShowInsuranceDetailsPrompt(false); navigate(`/cars/${selectedCarId}?tab=costs`); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('addCost.insurancePrompt.title') || 'Insurance details'}</DialogTitle>
          <DialogDescription>
            {t('addCost.insurancePrompt.message') || 'Since you just added insurance expenses, would you like to check and update your insurance details?'}
          </DialogDescription>
        </DialogHeader>
        {selectedCar && (
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{t('carDetail.sections.octa') || 'OCTA / Mandatory insurance'}</p>
              <dl className="grid grid-cols-1 gap-y-2 text-sm text-gray-700">
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.provider') || 'Provider'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.octaProvider || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.policyNumber') || 'Policy number'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.octaPolicyNumber || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.expiry') || 'Expiry'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.octaExpiry ? format(new Date(selectedCar.octaExpiry), 'dd MMM yyyy') : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.claimsHotline') || 'Claims hotline'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.octaClaimsHotline || '—'}</dd>
                </div>
              </dl>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 mb-2">{t('carDetail.sections.kasko') || 'KASKO / Comprehensive insurance'}</p>
              <dl className="grid grid-cols-1 gap-y-2 text-sm text-gray-700">
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.provider') || 'Provider'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.kaskoProvider || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.policyNumber') || 'Policy number'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.kaskoPolicyNumber || '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.expiry') || 'Expiry'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.kaskoExpiry ? format(new Date(selectedCar.kaskoExpiry), 'dd MMM yyyy') : '—'}</dd>
                </div>
                <div className="flex justify-between gap-2">
                  <dt className="text-gray-500">{t('carDetail.fields.claimsHotline') || 'Claims hotline'}</dt>
                  <dd className="font-medium text-gray-900 truncate">{selectedCar.kaskoClaimsHotline || '—'}</dd>
                </div>
              </dl>
            </div>
          </div>
        )}
        <div className="flex gap-2 justify-end mt-4">
          <Button variant="secondary" onClick={() => { setShowInsuranceDetailsPrompt(false); navigate(`/cars/${selectedCarId}?tab=costs`); }}>
            {t('addCost.insurancePrompt.later') || 'Later'}
          </Button>
          <Button onClick={() => { setShowInsuranceDetailsPrompt(false); navigate(`/cars/edit/${selectedCarId}`); }}>
            {t('addCost.insurancePrompt.editNow') || 'Edit Now'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default AddCostPage;
